import React from "react";
import { commitMutation, graphql } from "react-relay";

import type { DirectReport } from ".";
import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import uuidv4 from "uuid/v4";

import EmailListInput, { type Contact } from "~/common/EmailListInput";
import UserSelector, { type SingleValueType, type UserSelectorOption } from "~/common/UserSelect/UserSelector";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { Button, toast, Tooltip, useModals } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import { HelpCenterIcon, Modal } from "~/ui/legacyFellowIndex";

import type { TransferDirectReportModalMutation as MutationType } from "~/__generated__/TransferDirectReportModalMutation.graphql";

import styles from "./css/Modals.module.css";

interface Props {
	onClose: () => void;
	report: DirectReport;
	refetch: () => void;
	excludedEmails: string[];
	excludedUserIds: string[];
	internalInvitesAllowed: boolean;
	guestInvitesAllowed: boolean;
}

const TransferReportMutation = graphql`
	mutation TransferDirectReportModalMutation($reportId: ID!, $newManagerEmail: String!) {
		transferDirectReport(reportId: $reportId, newManagerEmail: $newManagerEmail) {
			user {
				internalAttributes {
					directReports {
						edges {
							node {
								...DirectReports_reportInfo
							}
						}
					}
				}
			}
		}
	}
`;

function ConfirmTransferModal({
	onClose,
	report,
	newManager,
	isLoading,
	analyticsProperties,
	onConfirm,
}: {
	onClose: () => void;
	report: DirectReport;
	newManager: Contact;
	isLoading: boolean;
	analyticsProperties: Record<string, unknown>;
	onConfirm: (closeInner: () => void) => void;
}) {
	const onDismiss = React.useCallback(() => {
		dispatchAnalyticsTrack("[Transfer Direct Report Modal] Dismissed Confirmation Modal", analyticsProperties);
		onClose();
	}, [analyticsProperties, onClose]);

	const onConfirmTransfer = React.useCallback(() => {
		onConfirm(onClose);
	}, [onConfirm, onClose]);

	return (
		<Modal onClose={onDismiss}>
			<Modal.Header noBottomBorder onClose={onDismiss}>
				<Modal.Title>Transfer direct report</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<p className={styles.bodyText}>
					Are you sure you want to transfer <strong style={{ overflowWrap: "anywhere" }}>{report.fullName}</strong> as a
					direct report to <strong>{newManager.fullName ?? newManager.email}</strong>?
				</p>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={onDismiss}>
					Cancel
				</Button>
				<Button variant="primary" disabled={isLoading} onClick={onConfirmTransfer}>
					Yes {isLoading && <FontAwesomeIcon icon={faSpinnerThird} spin />}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export function TransferDirectReportModal({
	onClose,
	report,
	refetch,
	excludedEmails,
	excludedUserIds,
	internalInvitesAllowed,
	guestInvitesAllowed,
}: Props) {
	const modals = useModals();
	const [newManager, setNewManager] = React.useState<Contact | undefined>(undefined);
	const [selectedManager, setSelectedManager] = React.useState<UserSelectorOption | undefined>(undefined);
	const [isLoading, setIsLoading] = React.useState(false);

	const analyticsProperties = React.useMemo(
		() => ({
			fingerprint: uuidv4(),
		}),
		[],
	);

	React.useEffect(() => {
		dispatchAnalyticsTrack("[Transfer Direct Report Modal] Opened Modal", analyticsProperties);
	}, [analyticsProperties]);

	const onDismiss = React.useCallback(() => {
		dispatchAnalyticsTrack("[Transfer Direct Report Modal] Dismissed Modal", analyticsProperties);
		onClose();
	}, [analyticsProperties, onClose]);

	const onManagerChange = React.useCallback((val: readonly Contact[] | Contact | null | undefined) => {
		const manager = val ? ("length" in val ? val[0] : val) : null;
		if (!manager) return;
		setNewManager({ id: manager.id, fullName: manager.fullName, email: manager.email });
	}, []);

	const onManagerChangeUserSelector = React.useCallback((val: SingleValueType<UserSelectorOption>) => {
		if (!val || !val.email) return;
		setNewManager({ id: val.value, email: val.email, fullName: val.label });
		setSelectedManager(val);
	}, []);

	const onConfirmTransfer = React.useCallback(
		(closeInner: () => void) => {
			if (!newManager) return;
			setIsLoading(true);
			commitMutation<MutationType>(environment, {
				mutation: TransferReportMutation,
				variables: {
					reportId: report.id,
					newManagerEmail: newManager.email,
				},
				onCompleted: (_data, errors) => {
					setIsLoading(false);

					if (errors?.length) {
						dispatchAnalyticsTrack(
							"[Transfer Direct Report Modal] Failed to Transfer Direct Report",
							analyticsProperties,
						);
						errors.forEach(error => toast.error(error.message));
						return;
					}

					refetch();
					dispatchAnalyticsTrack(
						"[Transfer Direct Report Modal] Successfully Transferred Direct Report",
						analyticsProperties,
					);
					toast.success(`${report.fullName} now reports to ${newManager.fullName}`, {
						title: "Direct report successfully transferred",
					});
					closeInner();
					onClose();
				},
			});
		},
		[newManager, analyticsProperties, report, refetch, onClose],
	);

	const onTransfer = React.useCallback(() => {
		if (!newManager) return;
		modals.open(({ close }) => (
			<ConfirmTransferModal
				onClose={close}
				report={report}
				onConfirm={onConfirmTransfer}
				newManager={newManager}
				isLoading={isLoading}
				analyticsProperties={analyticsProperties}
			/>
		));
	}, [modals, report, newManager, onConfirmTransfer, isLoading, analyticsProperties]);

	return (
		<Modal onClose={onDismiss} className={styles.modal}>
			<Modal.Header>
				<Modal.Title>Transfer direct report</Modal.Title>
				<Modal.HeaderActions onClose={onDismiss}>
					<HelpCenterIcon articleId="6739050" className={styles.helpLink} title="Managing your direct reports" />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body className={styles.modalBody}>
				<p className={styles.bodyText}>Select a new manager for this direct report</p>
				<div className={styles.reportDisplay}>
					<Avatar style={{ marginRight: "0.5em" }} user={report} />
					<div className={styles.reportInfo}>
						<p className={styles.name}>{report.fullName}</p>
						<p className={styles.email}>{report.email}</p>
					</div>
				</div>
				<h6 className={styles.selectHeading}>
					New manager{" "}
					<Tooltip
						placement="top"
						content={
							<div style={{ textAlign: "center", maxWidth: "195px" }}>
								The new manager will be notified of this transfer via email
							</div>
						}
					>
						<FontAwesomeIcon icon={faInfoCircle} className={styles.subText} />
					</Tooltip>
				</h6>
				{internalInvitesAllowed ? (
					<>
						<EmailListInput
							useNewTeammateBtn
							doesNotReportUnder={report.id}
							internalInvitesAllowed={internalInvitesAllowed}
							externalInvitesAllowed={false}
							guestInvitesAllowed={guestInvitesAllowed}
							exclude={excludedEmails}
							placeholder="Add new manager by name or email"
							onChange={onManagerChange}
						/>
						<small className={styles.subText}>New teammates will be invited to join Fellow.</small>
					</>
				) : (
					<UserSelector
						isClearable={false}
						doesNotReportUnder={report.id}
						selectedUser={selectedManager}
						excludeUserIDs={excludedUserIds}
						placeholder="Add a new manager"
						onChange={onManagerChangeUserSelector}
					/>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={onDismiss}>
					Cancel
				</Button>
				<Button variant="primary" disabled={!newManager} onClick={onTransfer}>
					Transfer
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
